import { Box, Grid, IconButton, ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Typography } from '@material-ui/core';
import { Button, DataGrid, GridColumnProps, StandardDialog, StatusChip, StatusType, Toolbar, DateField, PDFPreview, SelectSearchFloatingFilterComponent, SelectFilterComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { MoreVert, Delete, FindInPage, CloudDownload, Tune } from '@material-ui/icons';
import { MakeAnAdjustmentModal } from '../makeAnAdjustmentModal';
import { RemoveReceivableModal } from './removeReceivableModal';
import { formatDate } from '@roc/feature-utils';
import { formatCurrency, noOpComparator, filterProps, selectSearchFilterProps } from '@roc/ui/utils';
import { ToolbarInfoItem } from '../toolbarInfoItem';
import { PayableReceivablesTypes, TYPES_OF_FEE } from '../../types/types';
import { useReceivablesDashboardStore } from '../../hooks/useReceivablesDasboardStore';
import { format } from 'date-fns';
import { useDocumentStore } from '@roc/feature-documents';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '60vh',
    overflow: 'hidden',
  },
  containerBox: {
    display: 'flex',
  },
  infoItemsBox: {
    marginRight: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '30px'
  },
}));

export const ReceivableBillDetailsModal = observer(() => {
  const { receivablesBillsStore,
    receivablesBillsStore: {
      detailsModalOpen,
      selectedRecord,
      counterPartyExpenseGridStore,
    }
  } = useReceivablesDashboardStore()
  const [paymentDate, setPaymentDate] = useState(null);
  const pendingStatuses = [PayableReceivablesTypes.PENDING, PayableReceivablesTypes.BILLED]
  const isApproval = selectedRecord?.status === PayableReceivablesTypes.PENDING;


  useEffect(() => {
    setPaymentDate(null);
  }, [detailsModalOpen])

  const getContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='body1'>Please review all of the receivables conglomerated in this Bill. </Typography>
        </Grid>
        <Grid item xs={12}>
          <ReceivableBillDetailsGrid />
        </Grid>
        {!isApproval && (
          <Grid item container xs={12}>
            <Grid item alignContent='center' xs={2}>
              <Typography style={{ paddingBottom: '5px' }} variant="h6">Choose a payment date</Typography>
            </Grid>
            <Grid item xs={4}>
              <DateField
                inputVariant="outlined"
                standaloneLabel
                format='MM/dd/yyyy'
                fullWidth
                value={paymentDate}
                testId={'paymentDate'}
                onChange={date => setPaymentDate(date)}
              />
            </Grid>
          </Grid>)}
      </Grid>
    );
  }

  const performAction = async (status, paymentDate) => {
    await receivablesBillsStore.performBillAction(selectedRecord.recordId, status, paymentDate);
    receivablesBillsStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    handleCLoseModal();
  }

  const handleCLoseModal = () => {
    receivablesBillsStore.closeModal();
    counterPartyExpenseGridStore.resetAndFetchGridData();
  }

  return (
    <StandardDialog
      open={detailsModalOpen}
      title={`Bill ${selectedRecord?.billId}`}
      maxWidth="lg"
      handleClose={handleCLoseModal}
      dialogContent={getContent}
      dialogActions={(
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={() => performAction(PayableReceivablesTypes.REJECTED, null)}
            color="primary"
            variant="outlined"
            testId="reject"
            disabled={!pendingStatuses.includes(selectedRecord?.status)}
          >
            Reject Bill
          </Button>
          <Button
            onClick={() => performAction(
              isApproval ? PayableReceivablesTypes.BILLED : PayableReceivablesTypes.PAID,
              paymentDate == null ? null : format(paymentDate, 'yyyy-MM-dd')
            )
            }
            color="primary"
            variant="contained"
            testId="approve"
            disabled={!pendingStatuses.includes(selectedRecord?.status)}
          >
            {selectedRecord?.status === PayableReceivablesTypes.PENDING ? 'Approve' : 'Mark as Paid'}
          </Button>
        </>
      )}
    />
  );

});


const ReceivableBillDetailsGrid = observer(() => {
  const classes = useStyles();
  const { receivablesBillsStore,
    receivablesBillsStore: { receivableDetailsGridStore, selectedRecord, counterPartyExpenseGridStore },
    receivablesDashboardStore,
  } = useReceivablesDashboardStore()
  const { payablesApprovedStore: { vendorOptions } } = usePayablesDashboardStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [record, setRecord] = useState<any>({});
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentRecord, setAdjustmentRecord] = useState<any>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeRecord, setRemoveRecord] = useState<any>(null);
  const { documentStore } = useDocumentStore();

  useEffect(() => {
    receivableDetailsGridStore.resetAndFetchGridData();
  }, [selectedRecord]);

  const frameworkComponents = {
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value, 'MM/dd/yyyy') : '';
    },
    currencyCellRenderer({ value }) {
      return formatCurrency(value);
    },
    actionCellRenderer: params => {
      return (
        <>
          <IconButton onClick={e => {
            openMenu(e, params.node.data)
          }}>
            <MoreVert color="primary" />
          </IconButton>
        </>
      );
    },
    statusCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.PROTECTIVE_ADVANCES}
            label={params.value}
            size="small"
          />
        )
      );
    },
    selectSearchFloatingFilter: SelectSearchFloatingFilterComponent,
    selectFilter: SelectFilterComponent,
  };

  const COLUMNS: GridColumnProps[] = [
    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'vendorName',
      headerName: 'Vendor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => vendorOptions),
    },
    {
      field: 'invoice',
      headerName: 'Invoice #',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'feeType',
      headerName: 'Type',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(TYPES_OF_FEE, null)
    },
    {
      field: 'loanDealName',
      headerName: 'Deal Name',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'originalAmount',
      headerName: 'Original Amount',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'adjustment',
      headerName: 'Receivable Adjustment',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'amount',
      headerName: 'Amount Due',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorAmountDue',
      headerName: 'Investor Amount Due',
      minWidth: 145,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];


  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setRecord(record)
    setAnchorEl(event.currentTarget);
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    receivableDetailsGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    receivableDetailsGridStore.setFilterModel(filterModel);
  };

  return (
    <>
      <Box>
        <Grid item className={classes.gridContainer}>
          <DataGrid
            columns={COLUMNS}
            rows={receivableDetailsGridStore.gridData.data.rows}
            frameworkComponents={frameworkComponents}
            suppressRowClickSelection={true}
            toolbar={<ReceivableBillDetailsToolbar />}
            key={selectedRecord?.recordId}
            onSortChanged={onSortChanged}
            isLoading={receivableDetailsGridStore.isLoading}
            sortModel={receivableDetailsGridStore.sortModel}
            paginationData={receivableDetailsGridStore.gridData.meta}
            setPageNumber={receivableDetailsGridStore.setPageNumber}
            onFilterChanged={onFilterChanged}
            showFilters={receivableDetailsGridStore.showFilters}
            filterModel={receivableDetailsGridStore.filterModel}
          />
        </Grid>
        <Menu
          id="menu-appbar"
          getContentAnchorEl={null}
          anchorEl={anchorEl}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem
            disabled={!record.documentId}
            onClick={async () => {
              await receivablesBillsStore.getDocument(record.documentId);
              await documentStore.previewDocumentV2(receivablesBillsStore.currentDocument);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <FindInPage fontSize="small" />
            </ListItemIcon>
            <ListItemText>Preview File</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={!record.documentId}
            onClick={() => {
              const document = {
                taskId: record.documentId,
                originalFileName: record.originalFileName,
              };
              documentStore.downloadDocumentOriginalNameById(document);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <CloudDownload fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Invoice</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={selectedRecord.status == PayableReceivablesTypes.REJECTED || selectedRecord.status == PayableReceivablesTypes.PAID}
            onClick={() => {
              setAdjustmentRecord(record);
              setShowAdjustmentModal(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Tune fontSize="small" />
            </ListItemIcon>
            <ListItemText>Make an Adjustment</ListItemText>
          </MenuItem>
          <MenuItem
            disabled={selectedRecord.status == PayableReceivablesTypes.REJECTED || selectedRecord.status == PayableReceivablesTypes.PAID}
            onClick={() => {
              setRemoveRecord(record);
              setShowRemoveModal(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      <PDFPreview
        open={documentStore.documentPreviewV2 != null}
        title={documentStore.documentPreviewV2?.title}
        data={documentStore.documentPreviewV2?.data}
        dataV2={documentStore.documentPreviewV2?.data}
        headers={documentStore.documentPreviewV2?.headers}
        onClose={() => documentStore.closeDocumentPreviewV2()}
      />
      <MakeAnAdjustmentModal
        open={showAdjustmentModal}
        record={adjustmentRecord}
        onClose={() => setShowAdjustmentModal(false)}
        onSubmit={async (amount, notes) => {
          await receivablesDashboardStore.updateReceivableAdjustment(adjustmentRecord.recordId, amount, notes);
          receivableDetailsGridStore.resetAndFetchGridData();
          counterPartyExpenseGridStore.resetAndFetchGridData
        }}
      />
      <RemoveReceivableModal
        open={showRemoveModal}
        record={removeRecord}
        onClose={() => setShowRemoveModal(false)}
        onSubmit={async (comment) => {
          await receivablesBillsStore.removeReceivableFromBill(removeRecord.recordId, comment);
          receivableDetailsGridStore.resetAndFetchGridData();
        }}
      />
    </>

  );

});

const ReceivableBillDetailsToolbar = observer(({ }) => {
  const classes = useStyles();
  const { receivablesBillsStore: { selectedRecord } } = useReceivablesDashboardStore()
  return (
    <Grid container>
      <Grid item xs={12}>
        <Toolbar left={
          <Typography variant='h6'>Bills </Typography>
        }>
          <Box className={classes.containerBox} >
            <Box className={classes.infoItemsBox}>
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.receivableOriginalAmount}`} label={"Original Amount"} />
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.receivableAdjustment}`} label={"Adjustment Amount"} />
              <ToolbarInfoItem value={`${'$'}${selectedRecord?.amount}`} label={"Total Amount Due"} />
            </Box>
          </Box>
        </Toolbar>
      </Grid>
    </Grid>
  );
});
